'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';

export default function AuthContent() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] bg-primary p-10 xl:p-14 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />
          <div className="absolute top-1/2 left-1/2 w-[500px] h-[500px] rounded-full bg-white/3 -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-bold text-white text-xl tracking-tight">SmartEmailHub</span>
        </div>

        {/* Main copy */}
        <div className="relative space-y-8">
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight text-balance mb-4">
              Alle deine E-Mails.<br />Ein intelligenter Hub.
            </h2>
            <p className="text-blue-100 text-base leading-relaxed max-w-sm">
              Verbinde Gmail, Outlook, iCloud und mehr — und lass KI die Arbeit erledigen.
            </p>
          </div>

          {/* Feature list */}
          <ul className="space-y-3">
            {[
              { icon: '✦', text: 'KI priorisiert deine wichtigsten E-Mails automatisch' },
              { icon: '⚡', text: 'Smart Replies in einem Klick verfassen' },
              { icon: '🔕', text: 'Inbox Zero mit Snooze & One-Click Unsubscribe' },
              { icon: '🔗', text: 'HubSpot, Kalender & Aufgaben direkt verknüpft' },
            ]?.map((f, i) => (
              <li key={`feature-${i + 1}`} className="flex items-start gap-3 text-sm text-blue-100">
                <span className="text-white text-base mt-0.5 flex-shrink-0">{f?.icon}</span>
                {f?.text}
              </li>
            ))}
          </ul>

          {/* Email provider logos */}
          <div>
            <p className="text-xs text-blue-200 mb-3 font-medium uppercase tracking-wider">Unterstützte Anbieter</p>
            <div className="flex items-center gap-3">
              {[
                { name: 'Gmail', color: 'bg-red-500', letter: 'G' },
                { name: 'Outlook', color: 'bg-blue-500', letter: 'O' },
                { name: 'iCloud', color: 'bg-gray-500', letter: 'i' },
                { name: 'SMTP', color: 'bg-emerald-600', letter: '✉' },
              ]?.map((p) => (
                <div
                  key={`provider-${p?.name}`}
                  className={`w-9 h-9 rounded-lg ${p?.color} flex items-center justify-center text-white text-sm font-bold shadow-md`}
                  title={p?.name}
                >
                  {p?.letter}
                </div>
              ))}
              <span className="text-blue-200 text-xs ml-1">und mehr</span>
            </div>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
            <div className="flex -space-x-2">
              {['#60A5FA', '#34D399', '#F87171', '#FBBF24']?.map((c, i) => (
                <div
                  key={`avatar-${i + 1}`}
                  className="w-7 h-7 rounded-full border-2 border-primary flex items-center justify-center text-white text-2xs font-bold"
                  style={{ backgroundColor: c }}
                >
                  {['MK', 'JS', 'LB', 'TR']?.[i]}
                </div>
              ))}
            </div>
            <div>
              <p className="text-white text-xs font-semibold">12.400+ aktive Nutzer</p>
              <p className="text-blue-200 text-2xs">Durchschnittlich 2,3 Std./Woche gespart</p>
            </div>
          </div>
        </div>

        <p className="relative text-blue-200 text-xs">
          © 2026 SmartEmailHub · smartemailhub.de
        </p>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="font-bold text-foreground text-lg">SmartEmailHub</span>
        </div>

        <div className="w-full max-w-md">
          {/* Mode tabs */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'login' ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Anmelden
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'signup' ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Registrieren
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToSignUp={() => setMode('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}